import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { DateTimePicker } from '@/components/ui/shadcn/custom/DateTimePicker/date-time-picker';
import { Textarea } from '@/components/ui/textarea';
import { ComboboxField } from 'components/DesignSystems/ComboboxField/ComboboxField';
import FormGeoLocationField from 'components/IAP/formFields/FormGeoLocationField';
import type {
  CustomEventCategory,
  CustomEventSeverity,
  CustomEventStatus,
} from 'hooks/customEvents/useCustomEvents';
import { useAors } from 'hooks/useAors';
import { toOptions, type EventFormValues } from './eventForm';

const EVENT_CATEGORIES: CustomEventCategory[] = [
  'tropical_storm',
  'flood',
  'wildfire',
  'infrastructure',
  'threshold',
  'other',
];
const EVENT_SEVERITIES: CustomEventSeverity[] = ['low', 'medium', 'high'];
const EVENT_STATUSES: CustomEventStatus[] = [
  'active',
  'monitoring',
  'demobilizing',
];

const EventDetailsSection: React.FC = () => {
  const {
    register,
    control,
    getValues,
    formState: { errors },
  } = useFormContext<EventFormValues>();
  const { data: aors } = useAors();

  return (
    <div className="tw:grid tw:gap-4">
      <Field data-invalid={!!errors.name}>
        <FieldLabel htmlFor="new-event-name">Name *</FieldLabel>
        <Input
          id="new-event-name"
          aria-invalid={!!errors.name}
          {...register('name', {
            validate: (value) => value.trim().length > 0 || 'Name is required',
            maxLength: { value: 500, message: 'Name is too long' },
          })}
        />
        <FieldError errors={[errors.name]} />
      </Field>
      <div className="tw:grid tw:grid-cols-2 tw:gap-4">
        <Controller
          control={control}
          name="category"
          rules={{ required: 'Category is required' }}
          render={({ field }) => (
            <Field data-invalid={!!errors.category}>
              <ComboboxField
                label="Category *"
                options={toOptions(EVENT_CATEGORIES)}
                value={field.value ?? null}
                onChange={field.onChange}
              />
              <FieldError errors={[errors.category]} />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="severity"
          rules={{ required: 'Severity is required' }}
          render={({ field }) => (
            <Field data-invalid={!!errors.severity}>
              <ComboboxField
                label="Severity *"
                options={toOptions(EVENT_SEVERITIES)}
                value={field.value ?? null}
                onChange={field.onChange}
              />
              <FieldError errors={[errors.severity]} />
            </Field>
          )}
        />
      </div>
      <Field data-invalid={!!errors.startDatetime}>
        <FieldLabel htmlFor="new-event-start">Start *</FieldLabel>
        <Controller
          control={control}
          name="startDatetime"
          rules={{ required: 'Start date is required' }}
          render={({ field }) => (
            <DateTimePicker
              id="new-event-start"
              className="tw:w-full"
              aria-invalid={!!errors.startDatetime}
              defaultDateTime={field.value}
              onValueChange={field.onChange}
            />
          )}
        />
        <FieldError errors={[errors.startDatetime]} />
      </Field>
      <Field data-invalid={!!errors.expectedEndDatetime}>
        <FieldLabel htmlFor="new-event-expected-end">Expected End</FieldLabel>
        <Controller
          control={control}
          name="expectedEndDatetime"
          rules={{
            validate: (value) =>
              !value ||
              !dayjs(value).isBefore(dayjs(getValues('startDatetime'))) ||
              'Expected end must be after the start date',
          }}
          render={({ field }) => (
            <DateTimePicker
              id="new-event-expected-end"
              className="tw:w-full"
              aria-invalid={!!errors.expectedEndDatetime}
              defaultDateTime={field.value}
              onValueChange={field.onChange}
            />
          )}
        />
        <FieldError errors={[errors.expectedEndDatetime]} />
      </Field>
      <Controller
        control={control}
        name="status"
        rules={{ required: 'Status is required' }}
        render={({ field }) => (
          <Field data-invalid={!!errors.status}>
            <ComboboxField
              label="Status *"
              options={toOptions(EVENT_STATUSES)}
              value={field.value ?? null}
              onChange={field.onChange}
            />
            <FieldError errors={[errors.status]} />
          </Field>
        )}
      />
      <Field>
        <FieldLabel htmlFor="new-event-summary">Summary</FieldLabel>
        <Textarea id="new-event-summary" rows={3} {...register('summary')} />
      </Field>
      <Field>
        <FieldLabel htmlFor="new-event-description">Description</FieldLabel>
        <Textarea
          id="new-event-description"
          rows={3}
          {...register('description')}
        />
      </Field>
      <Controller
        control={control}
        name="aorIds"
        render={({ field }) => (
          <ComboboxField
            label="Areas of Responsibility"
            multiple
            options={(aors ?? []).map((aor) => ({
              value: aor.id,
              label: aor.name,
            }))}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <FormGeoLocationField name="location" label="Event Area" />
    </div>
  );
};

export default EventDetailsSection;
